import PageHeader from "@/components/PageHeader";
import { CtaBanner } from "@/components/CTA";
import Seo from "@/components/Seo";
import { motion } from "framer-motion";
import {
  CheckCircle2, X as XIcon, MapPin, Users, Timer, DollarSign,
  Repeat, Radar, Gauge, Layers, ShieldCheck, HeartHandshake,
  Building2, Landmark, HandCoins, Lock, Unlink,
} from "lucide-react";

const fade = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.55 },
};

const chip = "inline-flex items-center gap-1 text-[10px] uppercase tracking-widest rounded-full px-2.5 py-1 font-bold border";

/* ------------------------------------------------------------------ */
/* A — The Economics of Connection                                    */
/* ------------------------------------------------------------------ */
function EconomicsOfConnection() {
  return (
    <section className="py-20 lg:py-28 bg-white border-b border-borderSubtle" data-testid="impact-economics-section">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-start">
        <motion.div {...fade} className="lg:col-span-7">
          <p className="eyebrow text-gold-deep">The Economics of Connection</p>
          <h2 className="mt-4 font-display font-black text-navy tracking-tight leading-[1.05] text-3xl sm:text-4xl lg:text-[2.6rem]">
            Better Connections. Better Outcomes. Better Understanding of Veteran-Service Investments.
          </h2>
          <div className="mt-6 space-y-4 text-slateSoft leading-relaxed">
            <p>Across the United States, billions of dollars are invested in programs and services supporting veterans and their families.</p>
            <p className="text-navy font-semibold">Yet an important question remains: how effectively do these investments translate into meaningful access to support?</p>
            <p>Veteran-serving organizations frequently operate across separate systems, funding structures, and reporting requirements. A referral may be recorded without establishing whether the veteran successfully connected with a provider.</p>
            <p>Warm Hand Off LLC is developing a coordinated approach that combines human engagement, responsible AI, and evidence-based follow-through.</p>
            <p>Our goal is to help communities understand not simply how many referrals are made, but whether meaningful connections occur, where service delivery breaks down, and how available resources can be used more effectively.</p>
          </div>
        </motion.div>

        {/* Referral activity vs verified connection */}
        <motion.aside {...fade} className="lg:col-span-5" data-testid="impact-referral-vs-connection">
          <div className="bg-cream-panel border border-borderSubtle rounded-lg p-6 lg:p-7">
            <p className="text-[10px] uppercase tracking-widest text-navy/50 font-bold mb-5">Referral activity is not a connection</p>
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-4 bg-white border border-dashed border-navy/25 rounded-md" data-testid="impact-referral-row">
                <Unlink className="w-5 h-5 text-navy/40 mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-navy text-sm">A referral is recorded</p>
                  <p className="text-xs text-slateSoft mt-0.5">A name is passed along. Whether the veteran ever connected is unknown.</p>
                  <span className={`${chip} mt-2 bg-navy/5 text-navy/50 border-navy/15`}><XIcon className="w-3 h-3" /> Outcome invisible</span>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-white border border-gold/50 rounded-md shadow-sm" data-testid="impact-connection-row">
                <CheckCircle2 className="w-5 h-5 text-gold-deep mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-navy text-sm">A connection is confirmed</p>
                  <p className="text-xs text-slateSoft mt-0.5">The veteran, provider, or an authorized source verifies that meaningful contact actually happened.</p>
                  <span className={`${chip} mt-2 bg-gold/15 text-gold-deep border-gold/40`}><CheckCircle2 className="w-3 h-3" /> Evidence-based</span>
                </div>
              </div>
            </div>
            <p className="mt-5 text-xs text-slateSoft leading-relaxed">WHO coordinates the workflow. People confirm the outcome. That difference is what makes coordination measurable.</p>
          </div>
        </motion.aside>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* B — Pittsburgh: Where We Begin                                     */
/* ------------------------------------------------------------------ */
const pittsburghMeasures = [
  "Confirmed service connections",
  "Time required to establish meaningful connections",
  "Provider response and coordination effort",
  "Failed referrals and recovery actions",
  "Unresolved veteran needs",
  "Geographic service gaps",
  "Coordination costs and operational efficiency",
];

function Pittsburgh() {
  return (
    <section className="py-20 lg:py-28 bg-cream-panel border-b border-borderSubtle" data-testid="impact-pittsburgh-section">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <motion.div {...fade} className="max-w-3xl">
          <p className="eyebrow text-gold-deep">Where we begin</p>
          <h2 className="mt-4 font-display font-black text-navy tracking-tight leading-[1.05] text-3xl sm:text-4xl lg:text-[2.6rem]">
            Building the Evidence in Pittsburgh.
          </h2>
          <div className="mt-6 space-y-4 text-slateSoft leading-relaxed">
            <p>WHO&rsquo;s initial pilot strategy focuses on the Greater Pittsburgh region.</p>
            <p>The Pittsburgh pilot is intended to examine how veterans navigate service networks, how participating organizations coordinate assistance, and what happens when referrals encounter barriers.</p>
            <p>Through appropriately authorized participation, WHO aims to measure:</p>
          </div>
        </motion.div>

        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="impact-pittsburgh-measures">
          {pittsburghMeasures.map((m, i) => (
            <motion.div
              key={m}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: i * 0.05 }}
              className="p-5 bg-white border border-borderSubtle rounded-md hover:border-gold transition-colors"
              data-testid={`impact-measure-${m.toLowerCase().replace(/\W+/g, "-").slice(0, 34)}`}
            >
              <span className={`${chip} bg-gold/15 text-gold-deep border-gold/40`}>Planned pilot measure</span>
              <p className="mt-3 font-semibold text-navy text-sm leading-snug">{m}</p>
            </motion.div>
          ))}
        </div>

        {/* Veteran entrepreneurship within the Pittsburgh narrative */}
        <motion.div {...fade} className="mt-10 p-6 bg-white border border-borderSubtle rounded-lg max-w-4xl" data-testid="impact-entrepreneurship-subsection">
          <p className="eyebrow text-gold-deep">Veteran Entrepreneurship: Continuing Pittsburgh&rsquo;s Tradition of Innovation</p>
          <div className="mt-4 space-y-4 text-sm text-slateSoft leading-relaxed">
            <p>Pittsburgh&rsquo;s history is shaped by individuals and organizations willing to build, innovate, and create new opportunities. Veteran entrepreneurs can contribute to that tradition by launching businesses, developing technologies, creating jobs, and strengthening their communities.</p>
            <p>WHO&rsquo;s planned approach recognizes that successful entrepreneurship often depends on access to interconnected resources — from early-stage business counseling to financing guidance, certification, procurement, and growth support. By helping veterans discover these resources and, in the future, evaluating how meaningful connections are established, WHO aims to contribute to a more accessible entrepreneurial ecosystem.</p>
            <p className="text-navy font-semibold">A measurement distinction we will keep: a confirmed connection to a business counselor is not evidence that a business was formed, financed, certified, or became profitable. Coordination outcomes and business outcomes are different things, and we will not blur them.</p>
          </div>
        </motion.div>

        <motion.p {...fade} className="mt-8 text-sm text-slateSoft leading-relaxed max-w-3xl" data-testid="impact-pilot-disclaimer">
          These measurements will help establish a baseline for understanding the effectiveness and economics of veteran-service coordination. The Pittsburgh pilot is a <span className="font-semibold text-navy">planned pilot and measurement strategy</span> — it is not yet operating, and no financial savings or outcome results are claimed.
        </motion.p>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* C — From Pittsburgh to National Impact                             */
/* ------------------------------------------------------------------ */
const stages = [
  { n: "01", t: "Pittsburgh Pilot", b: "Build credible, consent-based evidence in one community.", tag: "Starting point", live: true },
  { n: "02", t: "Regional Replication", b: "Repeat validated measurements across neighboring regions.", tag: "Future aspiration", live: false },
  { n: "03", t: "Multi-State Learning", b: "Compare coordination and accessibility across state contexts.", tag: "Future aspiration", live: false },
  { n: "04", t: "National Economic Intelligence", b: "Support a broader understanding of veteran-service investment.", tag: "Future aspiration", live: false },
];

function Progression() {
  return (
    <section className="relative py-20 lg:py-28 bg-navy-deep text-cream border-y border-white/5 overflow-hidden" data-testid="impact-progression-section">
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-gold/10 blur-3xl" />
        <div className="absolute inset-0 dot-grid opacity-[0.06]" />
      </div>
      <div className="relative max-w-[1240px] mx-auto px-6 lg:px-10">
        <motion.div {...fade} className="max-w-3xl">
          <p className="eyebrow text-gold">From Pittsburgh to national impact</p>
          <h2 className="mt-4 font-display font-black text-cream tracking-tight leading-[1.05] text-3xl sm:text-4xl lg:text-[2.6rem]">
            Local Evidence. National Possibilities.
          </h2>
          <div className="mt-6 space-y-4 text-cream/80 leading-relaxed">
            <p>The challenges of fragmented service coordination extend beyond any single community.</p>
            <p>With validated results, standardized measurements, appropriate partnerships, and strong privacy protections, WHO&rsquo;s approach could eventually support broader regional and national understanding of veteran-service delivery.</p>
            <p>Potential applications include identifying resource gaps, understanding coordination costs, comparing service accessibility, and informing more effective public and philanthropic investments.</p>
            <p className="text-cream font-semibold">Pittsburgh represents the starting point for building credible evidence — not a basis for prematurely claiming national outcomes.</p>
          </div>
        </motion.div>

        <div className="mt-12 relative" data-testid="impact-progression-graphic">
          <div className="absolute left-6 right-6 top-[26px] h-[2px] bg-gold/25 hidden lg:block" aria-hidden />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {stages.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className={`relative rounded-md p-5 border ${s.live ? "bg-gold/10 border-gold/50" : "bg-white/[0.04] border-white/10 border-dashed"}`}
                data-testid={`impact-stage-${s.t.toLowerCase().replace(/\W+/g, "-")}`}
              >
                <div className={`w-[52px] h-[52px] rounded-full flex items-center justify-center font-display font-black text-sm ${s.live ? "bg-gold text-navy" : "bg-white/10 text-cream/70"}`}>{s.n}</div>
                <p className="mt-4 font-display font-bold text-cream text-lg leading-tight">{s.t}</p>
                <p className="mt-2 text-sm text-cream/70 leading-relaxed">{s.b}</p>
                <span className={`${chip} mt-3 ${s.live ? "bg-gold/20 text-gold border-gold/40" : "bg-white/5 text-cream/60 border-white/15"}`}>{s.tag}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* D — WHO Economic Intelligence™                                     */
/* ------------------------------------------------------------------ */
const insightAreas = [
  { icon: <Radar className="w-5 h-5" />, t: "Service Demand Intelligence", b: "Understand categories of veteran needs and patterns of unmet demand." },
  { icon: <Repeat className="w-5 h-5" />, t: "Coordination Performance", b: "Examine referral progression, confirmed connections, delays, recovery, and unresolved cases." },
  { icon: <MapPin className="w-5 h-5" />, t: "Resource Accessibility", b: "Identify geographic gaps and barriers affecting veterans' ability to obtain services." },
  { icon: <Gauge className="w-5 h-5" />, t: "Economic Effectiveness", b: "Examine coordination costs, administrative burden, and cost per confirmed connection." },
];

function EconomicIntelligence() {
  return (
    <section className="py-20 lg:py-28 bg-white border-b border-borderSubtle" data-testid="impact-intelligence-section">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <motion.div {...fade} className="max-w-3xl">
          <p className="eyebrow text-gold-deep">WHO Economic Intelligence™</p>
          <h2 className="mt-4 font-display font-black text-navy tracking-tight leading-[1.05] text-3xl sm:text-4xl lg:text-[2.6rem]">
            Understanding the System Behind the Service.
          </h2>
          <div className="mt-6 space-y-4 text-slateSoft leading-relaxed">
            <p><span className="font-semibold text-navy">WHO Economic Intelligence™</span> is a <span className="font-semibold text-navy">proposed future capability</span> — not an operational product.</p>
            <p>Its intended purpose is to transform appropriately authorized, privacy-protected operational measurements into insights that help participating organizations understand service demand, coordination effectiveness, provider capacity, and resource gaps.</p>
          </div>
        </motion.div>

        <div className="mt-10 grid sm:grid-cols-2 gap-4" data-testid="impact-insight-areas">
          {insightAreas.map((a, i) => (
            <motion.div
              key={a.t}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: i * 0.05 }}
              className="p-6 bg-cream-panel border border-borderSubtle rounded-md hover:border-gold transition-colors"
              data-testid={`impact-insight-${a.t.toLowerCase().replace(/\W+/g, "-")}`}
            >
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-navy text-gold flex items-center justify-center shrink-0">{a.icon}</span>
                <p className="font-display font-bold text-navy text-lg leading-tight">{a.t}</p>
              </div>
              <p className="mt-3 text-sm text-slateSoft leading-relaxed">{a.b}</p>
              <span className={`${chip} mt-3 bg-navy/5 text-navy/60 border-navy/15`}>Prospective insight area</span>
            </motion.div>
          ))}
        </div>

        <motion.div {...fade} className="mt-8 flex items-start gap-3 p-5 bg-navy/5 border border-navy/10 rounded-md max-w-3xl" data-testid="impact-privacy-note">
          <Lock className="w-5 h-5 text-navy mt-0.5 shrink-0" />
          <p className="text-sm text-navy/80 leading-relaxed">No sensitive veteran records should be exposed or commercialized. Any future reporting must apply appropriate consent, access controls, aggregation, and privacy safeguards.</p>
        </motion.div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* E — Measuring What Matters                                         */
/* ------------------------------------------------------------------ */
const metrics = [
  { icon: <CheckCircle2 className="w-5 h-5" />, t: "Confirmed Connection Rate", b: "Of the warm handoffs made, how many result in a verified, meaningful connection — not just a recorded referral." },
  { icon: <Timer className="w-5 h-5" />, t: "Time to Meaningful Connection", b: "How long it takes from a stated need to a confirmed connection, so delays become visible instead of silent." },
  { icon: <DollarSign className="w-5 h-5" />, t: "Coordination Cost per Confirmed Connection", b: "The coordination effort required to achieve one verified connection — a foundation for understanding efficiency." },
  { icon: <Repeat className="w-5 h-5" />, t: "Recovery and Referral Drop-Off", b: "Where referrals stall, how often follow-up recovers them, and where people fall out of the process." },
  { icon: <Radar className="w-5 h-5" />, t: "Unmet Needs and Resource Gaps", b: "Which needs remain unresolved, and where services do not exist, are full, or are out of reach." },
  { icon: <Gauge className="w-5 h-5" />, t: "Provider Capacity and Responsiveness", b: "How participating organizations respond — acceptance, redirection, wait times, and available capacity." },
];

function MeasuringWhatMatters() {
  return (
    <section className="py-20 lg:py-28 bg-cream-panel border-b border-borderSubtle" data-testid="impact-measuring-section">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <motion.div {...fade} className="max-w-3xl">
          <p className="eyebrow text-gold-deep">Measuring what matters</p>
          <h2 className="mt-4 font-display font-black text-navy tracking-tight leading-[1.05] text-3xl sm:text-4xl lg:text-[2.6rem]">
            A Measurement Framework, Not a Scoreboard.
          </h2>
          <p className="mt-6 text-slateSoft leading-relaxed">These are the measurements the Pittsburgh pilot is designed to capture. They are presented as a framework — no pilot results exist yet, and none are implied here.</p>
        </motion.div>

        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="impact-metric-cards">
          {metrics.map((m, i) => (
            <motion.div
              key={m.t}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: i * 0.05 }}
              className="p-6 bg-white border border-borderSubtle rounded-lg hover:border-gold transition-colors"
              data-testid={`impact-metric-${m.t.toLowerCase().replace(/\W+/g, "-").slice(0, 36)}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="w-10 h-10 rounded-full bg-gold/15 text-gold-deep flex items-center justify-center shrink-0">{m.icon}</span>
                <span className={`${chip} bg-navy/5 text-navy/60 border-navy/15`}>Planned pilot measure</span>
              </div>
              <p className="mt-4 font-display font-bold text-navy text-lg leading-tight">{m.t}</p>
              <p className="mt-2 text-sm text-slateSoft leading-relaxed">{m.b}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* F — Value for the Veteran-Support Ecosystem                        */
/* ------------------------------------------------------------------ */
const audiences = [
  { icon: <HeartHandshake className="w-5 h-5" />, t: "Veterans and Families", b: "More transparent coordination, fewer avoidable handoff failures, and clearer follow-through." },
  { icon: <Building2 className="w-5 h-5" />, t: "Service Providers", b: "Better visibility into coordination activity, unresolved needs, and operational challenges." },
  { icon: <Landmark className="w-5 h-5" />, t: "Government and Community Agencies", b: "More useful evidence for planning, service accessibility, and resource allocation." },
  { icon: <HandCoins className="w-5 h-5" />, t: "Foundations and Institutional Funders", b: "Better understanding of unmet demand, coordination effectiveness, and potential investment priorities." },
];

function EcosystemValue() {
  return (
    <section className="py-20 lg:py-28 bg-white border-b border-borderSubtle" data-testid="impact-ecosystem-value-section">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <motion.div {...fade} className="max-w-3xl">
          <p className="eyebrow text-gold-deep">Value for the veteran-support ecosystem</p>
          <h2 className="mt-4 font-display font-black text-navy tracking-tight leading-[1.05] text-3xl sm:text-4xl lg:text-[2.6rem]">
            Evidence That Serves Everyone in the Network.
          </h2>
        </motion.div>
        <div className="mt-10 grid sm:grid-cols-2 gap-4" data-testid="impact-audience-cards">
          {audiences.map((a, i) => (
            <motion.div
              key={a.t}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: i * 0.05 }}
              className="p-6 bg-cream-panel border border-borderSubtle rounded-md hover:border-gold transition-colors"
              data-testid={`impact-audience-${a.t.toLowerCase().replace(/\W+/g, "-").slice(0, 36)}`}
            >
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-navy text-gold flex items-center justify-center shrink-0">{a.icon}</span>
                <p className="font-display font-bold text-navy text-lg leading-tight">{a.t}</p>
              </div>
              <p className="mt-3 text-sm text-slateSoft leading-relaxed">{a.b}</p>
            </motion.div>
          ))}
        </div>
        <motion.p {...fade} className="mt-6 text-xs text-slateSoft max-w-3xl">WHO does not promise guaranteed savings or outcomes. These are the ways coordinated, evidence-based follow-through is intended to help each part of the ecosystem.</motion.p>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* G — Our Commitment                                                 */
/* ------------------------------------------------------------------ */
function Commitment() {
  return (
    <section className="relative py-20 lg:py-28 bg-navy-deep text-cream overflow-hidden" data-testid="impact-commitment-section">
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <div className="absolute -bottom-40 -right-40 w-[520px] h-[520px] rounded-full bg-gold/10 blur-3xl" />
        <div className="absolute inset-0 dot-grid opacity-[0.06]" />
      </div>
      <div className="relative max-w-[1240px] mx-auto px-6 lg:px-10 text-center">
        <motion.div {...fade} className="max-w-4xl mx-auto">
          <ShieldCheck className="w-10 h-10 text-gold mx-auto" />
          <blockquote className="mt-6 font-display font-black text-cream tracking-tight leading-[1.15] text-2xl sm:text-3xl lg:text-4xl" data-testid="impact-commitment-quote">
            &ldquo;Data serves the mission before commerce. Evidence before inference. Meaningful connection before claims of success.&rdquo;
          </blockquote>
          <p className="mt-8 text-cream/80 leading-relaxed max-w-2xl mx-auto">
            WHO&rsquo;s approach prioritizes veteran dignity, privacy, transparency, consent, and the responsible use of technology. Economic intelligence exists to serve the mission — never the other way around.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-2" data-testid="impact-commitment-principles">
            {["Veteran dignity", "Privacy by design", "Transparency", "Consent-first", "Responsible AI"].map((p) => (
              <span key={p} className={`${chip} bg-white/5 text-cream/80 border-white/15`}>{p}</span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
export default function Impact() {
  return (
    <div data-testid="impact-page">
      <Seo
        title="Impact & Economic Intelligence · WHO"
        path="/impact"
        description="The Economics of Connection — how WHO plans to measure confirmed connections, coordination costs, and service gaps, from a planned Pittsburgh pilot toward national understanding. One Connection. One Veteran. One Family. One Community at a Time."
      />
      <PageHeader
        eyebrow="WHO — The Economics of Connection"
        title="Impact & Economic Intelligence."
        lede="One Connection. One Veteran. One Family. One Community at a Time. Our primary mission is serving veterans and their families — economic intelligence is a complementary benefit that can help providers, communities, funders, and policymakers improve the veteran-support ecosystem."
        testId="impact-header"
      />
      <EconomicsOfConnection />
      <Pittsburgh />
      <Progression />
      <EconomicIntelligence />
      <MeasuringWhatMatters />
      <EcosystemValue />
      <Commitment />
      <CtaBanner
        eyebrow="If not you, then WHO."
        title="Help us build the evidence — starting in Pittsburgh."
        primaryText="Become a Partner"
        primaryTo="/partners"
        secondaryText="Contact WHO"
        secondaryTo="/contact"
      />
    </div>
  );
}
